import React, { useCallback, useMemo, useState, type FC } from "react";
import { Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useGetCampaign } from "../../../../queries/dynamicBonuses/useGetCampaign";
import { useDeleteCampaign } from "../../../../queries/dynamicBonuses/useDeleteCampaign";
import { useUpdateCampaign } from "../../../../queries/dynamicBonuses/useUpdateCampaign";
import { toCampaignIn } from "../../../../queries/dynamicBonuses/toWriteBody";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import CreateCampaignWizard from "./CreateCampaignWizard/CreateCampaignWizard";
import DeleteDynamicBonusModal from "../controls/DeleteDynamicBonusModal";
import { campaignClonePath } from "../clonePaths";
import { campaignToWizardValues } from "./CreateCampaignWizard/toWizardCampaignIn";
import {
  DYNAMIC_BONUS_CAMPAIGN_STATUS,
  isSeriesRunCampaign,
  type CampaignIn,
} from "../types";
import { useCampaignEditLocks } from "../useCampaignEditLocks";
import styles from "../stylesDynamicBonuses.module.css";

type CampaignDeleteModalProps = {
  campaignId: string;
  campaignName: string;
  onClose: () => void;
  onDeleted: () => void;
};

const CampaignDeleteModal: FC<CampaignDeleteModalProps> = ({
  campaignId,
  campaignName,
  onClose,
  onDeleted,
}) => {
  const deleteMutation = useDeleteCampaign(campaignId);

  return (
    <DeleteDynamicBonusModal
      open
      resourceType="campaign"
      resourceName={campaignName}
      isPending={deleteMutation.isPending}
      onClose={onClose}
      onConfirm={() =>
        deleteMutation.mutate(undefined, { onSuccess: onDeleted })
      }
    />
  );
};

/**
 * Campaign editing reuses the create wizard — hydrate via
 * `campaignToWizardValues`, assemble `CampaignIn` on save, and PUT while
 * preserving immutable category / referral_config via `toCampaignIn`.
 * Series-stamped runs are view-only (BONENG-842).
 */
const EditCampaignPage: FC = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const id = campaignId ?? "";
  const { data: campaign, isLoading, isError } = useGetCampaign(id);
  const { mutate: updateCampaign, isPending } = useUpdateCampaign(id);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { isEnabled: duplicateBuilderEnabled } = usePamFeature(
    "dynamicBonusesDuplicateBuilder",
  );
  const { isBannerOnlyEdit, isFullyReadOnly } = useCampaignEditLocks(
    campaign?.status,
  );

  const initialValues = useMemo(
    () => (campaign ? campaignToWizardValues(campaign) : undefined),
    [campaign],
  );

  const handleSubmit = useCallback(
    (payload: CampaignIn) => {
      if (!campaign || isSeriesRunCampaign(campaign)) return;
      const writableCampaign = toCampaignIn(campaign);
      const updatePayload = isBannerOnlyEdit
        ? {
            ...writableCampaign,
            steps: writableCampaign.steps.map((step, index) => ({
              ...step,
              banner: payload.steps[index]?.banner ?? step.banner,
              display_locations:
                payload.steps[index]?.display_locations ??
                step.display_locations,
            })),
          }
        : toCampaignIn(campaign, {
            ...payload,
            category: campaign.category,
            referral_config: campaign.referral_config,
          });
      updateCampaign(
        updatePayload,
        {
          onSuccess: () => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS),
        },
      );
    },
    [campaign, isBannerOnlyEdit, updateCampaign, navigate],
  );

  if (isLoading || isError || !campaign) {
    return (
      <>
        <FeatureFlagGuard feature="dynamicBonuses" mode="route">
          {isLoading ? (
            <Spin data-testid="edit-campaign-loading" />
          ) : (
            <p className={styles.listError}>
              {AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_LOAD_ERROR}
            </p>
          )}
        </FeatureFlagGuard>
      </>
    );
  }

  const isSeriesRun = isSeriesRunCampaign(campaign);
  const isReadOnly = isSeriesRun || isFullyReadOnly;
  const rulesLocked = !isReadOnly && isBannerOnlyEdit;
  const canDelete =
    !isSeriesRun && campaign.status === DYNAMIC_BONUS_CAMPAIGN_STATUS.DRAFT;

  return (
    <>
      <CreateCampaignWizard
        title={
          isReadOnly
            ? AppStrings.PAM_DYNAMIC_BONUS_VIEW_CAMPAIGN
            : AppStrings.PAM_DYNAMIC_BONUS_EDIT_CAMPAIGN
        }
        submitLabel={
          isReadOnly ? undefined : AppStrings.PAM_DYNAMIC_BONUS_SAVE_CAMPAIGN
        }
        initialValues={initialValues}
        onSubmit={isReadOnly ? undefined : handleSubmit}
        isSubmitting={isReadOnly ? false : isPending}
        readOnly={isReadOnly}
        rulesLocked={rulesLocked}
        seriesId={campaign.series_campaign_id}
        editCampaign={
          isSeriesRun
            ? undefined
            : {
                campaignId: campaign.campaign_id,
                status: campaign.status,
              }
        }
        onDelete={canDelete ? () => setIsDeleteOpen(true) : undefined}
        onDuplicate={
          duplicateBuilderEnabled
            ? () => navigate(campaignClonePath(campaign.campaign_id))
            : undefined
        }
      />

      {isDeleteOpen && (
        <CampaignDeleteModal
          campaignId={campaign.campaign_id}
          campaignName={campaign.name}
          onClose={() => setIsDeleteOpen(false)}
          onDeleted={() =>
            navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS)
          }
        />
      )}
    </>
  );
};

export default EditCampaignPage;
